import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Database, MapPin, Loader2, AlertCircle, RefreshCw, X, Search } from 'lucide-react';
import { LwinReferenceControllerApi, Pageable, LwinReference, LwinReferenceFilterValues } from '../api';
import LwinChain from './LwinChain';
import { apiConfiguration } from "../api/apiConfig";

interface LwinReferencesProps {
    refreshKey?: number;
    onRefresh?: () => void;
}

const LwinReferences = ({ refreshKey, onRefresh }: LwinReferencesProps) => {
    const [lwinReferences, setLwinReferences] = useState<LwinReference[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedReferences, setExpandedReferences] = useState(new Set());

    // Pagination state
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state
    const [lwin, setLwin] = useState<number | undefined>(undefined);
    const [status, setStatus] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [producerTitle, setProducerTitle] = useState('');
    const [producerName, setProducerName] = useState('');
    const [wine, setWine] = useState('');
    const [country, setCountry] = useState('');
    const [region, setRegion] = useState('');
    const [subRegion, setSubRegion] = useState('');
    const [site, setSite] = useState('');
    const [parcel, setParcel] = useState('');
    const [colour, setColour] = useState('');
    const [type, setType] = useState('');
    const [subType, setSubType] = useState('');
    const [designation, setDesignation] = useState('');
    const [classification, setClassification] = useState('');
    const [vintageConfig, setVintageConfig] = useState('');
    const [firstVintageMin, setFirstVintageMin] = useState<number | undefined>(undefined);
    const [firstVintageMax, setFirstVintageMax] = useState<number | undefined>(undefined);
    const [finalVintageMin, setFinalVintageMin] = useState<number | undefined>(undefined);
    const [finalVintageMax, setFinalVintageMax] = useState<number | undefined>(undefined);
    const [dateAddedAfter, setDateAddedAfter] = useState('');
    const [dateAddedBefore, setDateAddedBefore] = useState('');
    const [dateUpdatedAfter, setDateUpdatedAfter] = useState('');
    const [dateUpdatedBefore, setDateUpdatedBefore] = useState('');

    // Draft values for filters
    const [draftLwin, setDraftLwin] = useState<number | undefined>(undefined);
    const [draftStatus, setDraftStatus] = useState('');
    const [draftDisplayName, setDraftDisplayName] = useState('');
    const [draftProducerTitle, setDraftProducerTitle] = useState('');
    const [draftProducerName, setDraftProducerName] = useState('');
    const [draftWine, setDraftWine] = useState('');
    const [draftCountry, setDraftCountry] = useState('');
    const [draftRegion, setDraftRegion] = useState('');
    const [draftSubRegion, setDraftSubRegion] = useState('');
    const [draftSite, setDraftSite] = useState('');
    const [draftParcel, setDraftParcel] = useState('');
    const [draftColour, setDraftColour] = useState('');
    const [draftType, setDraftType] = useState('');
    const [draftSubType, setDraftSubType] = useState('');
    const [draftDesignation, setDraftDesignation] = useState('');
    const [draftClassification, setDraftClassification] = useState('');
    const [draftVintageConfig, setDraftVintageConfig] = useState('');
    const [draftFirstVintageMin, setDraftFirstVintageMin] = useState<number | undefined>(undefined);
    const [draftFirstVintageMax, setDraftFirstVintageMax] = useState<number | undefined>(undefined);
    const [draftFinalVintageMin, setDraftFinalVintageMin] = useState<number | undefined>(undefined);
    const [draftFinalVintageMax, setDraftFinalVintageMax] = useState<number | undefined>(undefined);
    const [draftDateAddedAfter, setDraftDateAddedAfter] = useState('');
    const [draftDateAddedBefore, setDraftDateAddedBefore] = useState('');
    const [draftDateUpdatedAfter, setDraftDateUpdatedAfter] = useState('');
    const [draftDateUpdatedBefore, setDraftDateUpdatedBefore] = useState('');

    // Filter options from API
    const [filterOptions, setFilterOptions] = useState<LwinReferenceFilterValues>({
        statuses: [],
        producerNames: [],
        countries: [],
        regions: [],
        subRegions: [],
        colours: [],
        types: [],
        subTypes: [],
        classifications: [],
        vintageConfigs: []
    });

    // Collapsible section state
    const [collapsedSections, setCollapsedSections] = useState({
        textSearch: true,
        wineAttributes: true,
        origins: true,
        vintageFilters: true,
        dateFilters: true
    });

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            backgroundColor: 'var(--canvas)',
            minHeight: '100vh',
        },
        contentWrapper: {
            display: 'flex',
            gap: '24px',
            width: '100%',
        },
        mainContent: {
            flex: 1,
            minWidth: 0,
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: 'var(--ink-500)',
        },
        errorContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'var(--danger-bg)',
            border: '1px solid var(--danger-border)',
            borderRadius: '12px',
            padding: '32px',
        },
        errorContent: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
        },
        errorText: {
            color: 'var(--danger)',
            fontSize: '16px',
            fontWeight: '500' as const,
        },
        retryButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'var(--danger)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
        },
        referenceCard: {
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            marginBottom: '16px',
            overflow: 'hidden' as const,
            position: 'relative' as const,
            transition: 'all 0.2s ease',
        },
        referenceHeader: {
            padding: '20px 24px',
            cursor: 'pointer' as const,
            position: 'relative' as const,
        },
        referenceHeaderContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        referenceLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1,
        },
        referenceInfo: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '8px',
        },
        referenceTitle: {
            fontSize: '18px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            margin: 0,
        },
        referenceMeta: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            fontSize: '14px',
            color: 'var(--ink-500)',
        },
        referenceMetaItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        },
        statusBadge: (status: string) => ({
            backgroundColor: status === 'ACTIVE' ? 'var(--success-bg)' : 'var(--warning-bg)',
            color: status === 'ACTIVE' ? 'var(--success)' : 'var(--warning)',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500' as const,
        }),
        referenceRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        lwinBadge: {
            backgroundColor: 'var(--info-bg)',
            color: 'var(--info)',
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600' as const,
        },
        referenceDetails: {
            borderTop: '1px solid var(--border)',
            backgroundColor: 'var(--canvas)',
            padding: '20px 24px',
        },
        detailsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
        },
        detailItem: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px',
        },
        detailLabel: {
            fontSize: '12px',
            fontWeight: '500' as const,
            color: 'var(--ink-500)',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
        },
        detailValue: {
            fontSize: '14px',
            color: 'var(--ink-900)',
            fontWeight: '500' as const,
        },
        pagination: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '32px',
            padding: '16px 0',
        },
        pageButton: {
            padding: '8px 16px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
            color: 'var(--ink-700)',
        },
        pageInfo: {
            fontSize: '14px',
            color: 'var(--ink-500)',
        },
    };

    // Filter styles (similar to ProductClaude)
    const filterStyles = {
        sidebar: {
            width: '320px',
            flexShrink: 0,
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            height: '80vh',
            position: 'sticky' as const,
            top: '24px',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
        },
        filterPanel: {
            padding: '24px',
            overflowY: 'scroll' as const,
            flex: 1,
            height: '100%',
            scrollbarWidth: 'thin' as const,
            scrollbarColor: 'var(--ink-400) var(--hover)',
        },
        filterSectionTitle: {
            fontSize: '16px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer' as const,
            padding: '8px 0',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
        },
        filterSectionTitleHover: {
            backgroundColor: 'var(--hover)',
        },
        filterSectionContent: {
            overflow: 'hidden' as const,
            transition: 'max-height 0.3s ease-out',
        },
        filterSectionCollapsed: {
            maxHeight: '0px',
        },
        filterSectionExpanded: {
            maxHeight: '1000px',
        },
        filterGroup: {
            marginBottom: '16px',
        },
        filterLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500' as const,
            color: 'var(--ink-700)',
            marginBottom: '6px',
        },
        filterInput: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
            backgroundColor: 'var(--surface)',
        },
        filterSelect: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
            backgroundColor: 'var(--surface)',
            cursor: 'pointer' as const,
        },
        filterActions: {
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border)',
        },
        applyButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: 'var(--claret-600)',
            color: 'white',
            border: '1px solid var(--claret-600)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
            transition: 'background-color 0.2s',
        },
        applyButtonHover: {
            backgroundColor: 'var(--claret-700)',
        },
        filterClearButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: 'var(--border)',
            color: 'var(--ink-700)',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
        },
        filterCount: {
            backgroundColor: 'var(--danger)',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: '600' as const,
        },
    };

    // Load filter options
    useEffect(() => {
        const api = new LwinReferenceControllerApi(apiConfiguration);

        api.getLwinReferenceFilters()
            .then(({ data }) => {
                setFilterOptions(data);
            })
            .catch(err => {
                console.error('Failed to load filter options:', err);
            });
    }, []);

    // Load LWIN references from API
    useEffect(() => {
        const api = new LwinReferenceControllerApi(apiConfiguration);
        const pageable: Pageable = { page, size, sort: [] };

        setLoading(true);
        setError(null);

        api.getAllLwinReferences(
            pageable,
            lwin,
            status || undefined,
            displayName || undefined,
            producerTitle || undefined,
            producerName || undefined,
            wine || undefined,
            country || undefined,
            region || undefined,
            subRegion || undefined,
            site || undefined,
            parcel || undefined,
            colour || undefined,
            type || undefined,
            subType || undefined,
            designation || undefined,
            classification || undefined,
            vintageConfig || undefined,
            firstVintageMin,
            firstVintageMax,
            finalVintageMin,
            finalVintageMax,
            dateAddedAfter || undefined,
            dateAddedBefore || undefined,
            dateUpdatedAfter || undefined,
            dateUpdatedBefore || undefined
        )
        .then(({ data }) => {
            setLwinReferences(data.content ?? []);
            setTotalPages(data.totalPages ?? 1);
        })
        .catch(err => {
            setError('Failed to load LWIN references');
            console.error(err);
        })
        .finally(() => {
            setLoading(false);
        });
    }, [page, size, refreshKey, lwin, status, displayName, producerTitle, producerName, wine, country, region, subRegion, site, parcel, colour, type, subType, designation, classification, vintageConfig, firstVintageMin, firstVintageMax, finalVintageMin, finalVintageMax, dateAddedAfter, dateAddedBefore, dateUpdatedAfter, dateUpdatedBefore]);

    // Initialize draft values
    useEffect(() => {
        setDraftLwin(lwin);
        setDraftStatus(status);
        setDraftDisplayName(displayName);
        setDraftProducerTitle(producerTitle);
        setDraftProducerName(producerName);
        setDraftWine(wine);
        setDraftCountry(country);
        setDraftRegion(region);
        setDraftSubRegion(subRegion);
        setDraftSite(site);
        setDraftParcel(parcel);
        setDraftColour(colour);
        setDraftType(type);
        setDraftSubType(subType);
        setDraftDesignation(designation);
        setDraftClassification(classification);
        setDraftVintageConfig(vintageConfig);
        setDraftFirstVintageMin(firstVintageMin);
        setDraftFirstVintageMax(firstVintageMax);
        setDraftFinalVintageMin(finalVintageMin);
        setDraftFinalVintageMax(finalVintageMax);
        setDraftDateAddedAfter(dateAddedAfter);
        setDraftDateAddedBefore(dateAddedBefore);
        setDraftDateUpdatedAfter(dateUpdatedAfter);
        setDraftDateUpdatedBefore(dateUpdatedBefore);
    }, [lwin, status, displayName, producerTitle, producerName, wine, country, region, subRegion, site, parcel, colour, type, subType, designation, classification, vintageConfig, firstVintageMin, firstVintageMax, finalVintageMin, finalVintageMax, dateAddedAfter, dateAddedBefore, dateUpdatedAfter, dateUpdatedBefore]);

    const toggleSection = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const applyAllFilters = () => {
        setLwin(draftLwin);
        setStatus(draftStatus);
        setDisplayName(draftDisplayName);
        setProducerTitle(draftProducerTitle);
        setProducerName(draftProducerName);
        setWine(draftWine);
        setCountry(draftCountry);
        setRegion(draftRegion);
        setSubRegion(draftSubRegion);
        setSite(draftSite);
        setParcel(draftParcel);
        setColour(draftColour);
        setType(draftType);
        setSubType(draftSubType);
        setDesignation(draftDesignation);
        setClassification(draftClassification);
        setVintageConfig(draftVintageConfig);
        setFirstVintageMin(draftFirstVintageMin);
        setFirstVintageMax(draftFirstVintageMax);
        setFinalVintageMin(draftFinalVintageMin);
        setFinalVintageMax(draftFinalVintageMax);
        setDateAddedAfter(draftDateAddedAfter);
        setDateAddedBefore(draftDateAddedBefore);
        setDateUpdatedAfter(draftDateUpdatedAfter);
        setDateUpdatedBefore(draftDateUpdatedBefore);
        setPage(0);
    };

    const clearFilters = () => {
        setDraftLwin(undefined);
        setDraftStatus('');
        setDraftDisplayName('');
        setDraftProducerTitle('');
        setDraftProducerName('');
        setDraftWine('');
        setDraftCountry('');
        setDraftRegion('');
        setDraftSubRegion('');
        setDraftSite('');
        setDraftParcel('');
        setDraftColour('');
        setDraftType('');
        setDraftSubType('');
        setDraftDesignation('');
        setDraftClassification('');
        setDraftVintageConfig('');
        setDraftFirstVintageMin(undefined);
        setDraftFirstVintageMax(undefined);
        setDraftFinalVintageMin(undefined);
        setDraftFinalVintageMax(undefined);
        setDraftDateAddedAfter('');
        setDraftDateAddedBefore('');
        setDraftDateUpdatedAfter('');
        setDraftDateUpdatedBefore('');

        setLwin(undefined);
        setStatus('');
        setDisplayName('');
        setProducerTitle('');
        setProducerName('');
        setWine('');
        setCountry('');
        setRegion('');
        setSubRegion('');
        setSite('');
        setParcel('');
        setColour('');
        setType('');
        setSubType('');
        setDesignation('');
        setClassification('');
        setVintageConfig('');
        setFirstVintageMin(undefined);
        setFirstVintageMax(undefined);
        setFinalVintageMin(undefined);
        setFinalVintageMax(undefined);
        setDateAddedAfter('');
        setDateAddedBefore('');
        setDateUpdatedAfter('');
        setDateUpdatedBefore('');
        setPage(0);
    };

    const refreshData = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const toggleReference = (lwin: number | undefined) => {
        if (!lwin) return;
        const newExpanded = new Set(expandedReferences);
        if (newExpanded.has(lwin)) {
            newExpanded.delete(lwin);
        } else {
            newExpanded.add(lwin);
        }
        setExpandedReferences(newExpanded);
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB');
        } catch {
            return 'N/A';
        }
    };

    // Calculate active filter count
    const activeFilterCount = [
        lwin !== undefined ? 'lwin' : '',
        status, displayName, producerTitle, producerName, wine, country, region, subRegion, site, parcel, colour, type, subType, designation, classification, vintageConfig,
        firstVintageMin !== undefined ? 'firstVintageMin' : '',
        firstVintageMax !== undefined ? 'firstVintageMax' : '',
        finalVintageMin !== undefined ? 'finalVintageMin' : '',
        finalVintageMax !== undefined ? 'finalVintageMax' : '',
        dateAddedAfter, dateAddedBefore, dateUpdatedAfter, dateUpdatedBefore
    ].filter(Boolean).length;

    // Loading state
    if (loading && !lwinReferences.length) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <Loader2 size={32} className="animate-spin" color="var(--ink-900)" />
                    <p style={{marginTop: '16px', fontSize: '16px'}}>Loading LWIN references...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <div style={styles.errorContent}>
                        <AlertCircle size={20} color="var(--danger)" />
                        <span style={styles.errorText}>{error}</span>
                    </div>
                    <button
                        style={styles.retryButton}
                        onClick={refreshData}
                    >
                        <RefreshCw size={16} />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.contentWrapper}>
                {/* Sidebar Filter Panel */}
                <div style={filterStyles.sidebar}>
                    <div style={filterStyles.filterPanel}>
                        <div style={filterStyles.filterSectionTitle}>
                            LWIN Filters
                            {activeFilterCount > 0 && (
                                <span style={{ ...filterStyles.filterCount, marginLeft: '8px' }}>{activeFilterCount}</span>
                            )}
                        </div>

                        {/* Text Search Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            ...(collapsedSections.textSearch ? {} : filterStyles.filterSectionTitleHover)
                        }}
                        onClick={() => toggleSection('textSearch')}>
                            <span style={{ flex: 1 }}>Text Search</span>
                            {collapsedSections.textSearch ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.textSearch ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>LWIN</label>
                                <input
                                    type="number"
                                    value={draftLwin ?? ''}
                                    onChange={(e) => setDraftLwin(e.target.value ? Number(e.target.value) : undefined)}
                                    placeholder="Enter LWIN number"
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Display Name</label>
                                <input
                                    type="text"
                                    value={draftDisplayName}
                                    onChange={(e) => setDraftDisplayName(e.target.value)}
                                    placeholder="Search by display name"
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Producer Name</label>
                                <input
                                    type="text"
                                    value={draftProducerName}
                                    onChange={(e) => setDraftProducerName(e.target.value)}
                                    placeholder="Enter producer name"
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Wine</label>
                                <input
                                    type="text"
                                    value={draftWine}
                                    onChange={(e) => setDraftWine(e.target.value)}
                                    placeholder="Enter wine name"
                                    style={filterStyles.filterInput}
                                />
                            </div>
                        </div>

                        {/* Wine Attributes */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('wineAttributes')}>
                            <span style={{ flex: 1 }}>Wine Attributes</span>
                            {collapsedSections.wineAttributes ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.wineAttributes ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Status</label>
                                <select
                                    value={draftStatus}
                                    onChange={(e) => setDraftStatus(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Statuses</option>
                                    {filterOptions.statuses?.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Colour</label>
                                <select
                                    value={draftColour}
                                    onChange={(e) => setDraftColour(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Colours</option>
                                    {filterOptions.colours?.map(colour => (
                                        <option key={colour} value={colour}>{colour}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Type</label>
                                <select
                                    value={draftType}
                                    onChange={(e) => setDraftType(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Types</option>
                                    {filterOptions.types?.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Origins */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('origins')}>
                            <span style={{ flex: 1 }}>Origins</span>
                            {collapsedSections.origins ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.origins ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Country</label>
                                <select
                                    value={draftCountry}
                                    onChange={(e) => setDraftCountry(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Countries</option>
                                    {filterOptions.countries?.map(country => (
                                        <option key={country} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Region</label>
                                <select
                                    value={draftRegion}
                                    onChange={(e) => setDraftRegion(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Regions</option>
                                    {filterOptions.regions?.map(region => (
                                        <option key={region} value={region}>{region}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Vintage Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('vintageFilters')}>
                            <span style={{ flex: 1 }}>Vintage Filters</span>
                            {collapsedSections.vintageFilters ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.vintageFilters ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>First Vintage Range</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={draftFirstVintageMin ?? ''}
                                        onChange={(e) => setDraftFirstVintageMin(e.target.value ? Number(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, width: '100px' }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={draftFirstVintageMax ?? ''}
                                        onChange={(e) => setDraftFirstVintageMax(e.target.value ? Number(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, width: '100px' }}
                                    />
                                </div>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Final Vintage Range</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min"
                                        value={draftFinalVintageMin ?? ''}
                                        onChange={(e) => setDraftFinalVintageMin(e.target.value ? Number(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, width: '100px' }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max"
                                        value={draftFinalVintageMax ?? ''}
                                        onChange={(e) => setDraftFinalVintageMax(e.target.value ? Number(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, width: '100px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Date Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('dateFilters')}>
                            <span style={{ flex: 1 }}>Date Filters</span>
                            {collapsedSections.dateFilters ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.dateFilters ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Date Added After</label>
                                <input
                                    type="date"
                                    value={draftDateAddedAfter}
                                    onChange={(e) => setDraftDateAddedAfter(e.target.value)}
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Date Added Before</label>
                                <input
                                    type="date"
                                    value={draftDateAddedBefore}
                                    onChange={(e) => setDraftDateAddedBefore(e.target.value)}
                                    style={filterStyles.filterInput}
                                />
                            </div>
                        </div>

                        {/* Filter Actions */}
                        <div style={filterStyles.filterActions}>
                            <button
                                style={filterStyles.applyButton}
                                onClick={applyAllFilters}
                                onMouseEnter={(e) => Object.assign(e.currentTarget.style, filterStyles.applyButtonHover)}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = filterStyles.applyButton.backgroundColor;
                                }}
                            >
                                <Search size={14} />
                                Apply Filters
                            </button>

                            <button
                                style={filterStyles.filterClearButton}
                                onClick={clearFilters}
                            >
                                <X size={14} />
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content - LWIN Reference List */}
                <div style={styles.mainContent}>
                    {lwinReferences.map((reference, index) => (
                        <div
                            key={reference.lwin}
                            style={styles.referenceCard}
                        >
                            {/* Reference Header */}
                            <div
                                style={styles.referenceHeader}
                                onClick={() => toggleReference(reference.lwin)}
                            >
                                <div style={styles.referenceHeaderContent}>
                                    <div style={styles.referenceLeft}>
                                        {expandedReferences.has(reference.lwin) ?
                                            <ChevronDown size={20} color="var(--ink-500)" /> :
                                            <ChevronRight size={20} color="var(--ink-500)" />
                                        }
                                        <Database size={24} color="var(--claret-600)" />
                                        <div style={styles.referenceInfo}>
                                            <h2 style={styles.referenceTitle}>{reference.displayName}</h2>
                                            <div style={styles.referenceMeta}>
                                                <span style={styles.referenceMetaItem}>
                                                    <MapPin size={16} />
                                                    {reference.country}, {reference.region}
                                                </span>
                                                <span>{reference.producerName}</span>
                                                <span style={styles.statusBadge(reference.status || '')}>
                                                    {reference.status}
                                                </span>
                                                {reference.status === 'Combined' && reference.lwin != null && (
                                                    <LwinChain lwin={reference.lwin} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={styles.referenceRight}>
                                        <span style={styles.lwinBadge}>
                                            LWIN {reference.lwin}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Details */}
                            {expandedReferences.has(reference.lwin) && (
                                <div style={styles.referenceDetails}>
                                    <div style={styles.detailsGrid}>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Producer Title</span>
                                            <span style={styles.detailValue}>{reference.producerTitle || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Wine</span>
                                            <span style={styles.detailValue}>{reference.wine || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Sub Region</span>
                                            <span style={styles.detailValue}>{reference.subRegion || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Site</span>
                                            <span style={styles.detailValue}>{reference.site || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Colour</span>
                                            <span style={styles.detailValue}>{reference.colour || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Type</span>
                                            <span style={styles.detailValue}>{reference.type || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Classification</span>
                                            <span style={styles.detailValue}>{reference.classification || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Vintage Config</span>
                                            <span style={styles.detailValue}>{reference.vintageConfig || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>First Vintage</span>
                                            <span style={styles.detailValue}>{reference.firstVintage || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Final Vintage</span>
                                            <span style={styles.detailValue}>{reference.finalVintage || 'N/A'}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Date Added</span>
                                            <span style={styles.detailValue}>{formatDate(reference.dateAdded)}</span>
                                        </div>
                                        <div style={styles.detailItem}>
                                            <span style={styles.detailLabel}>Date Updated</span>
                                            <span style={styles.detailValue}>{formatDate(reference.dateUpdated)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Pagination */}
                    <div style={styles.pagination}>
                        <button
                            style={{
                                ...styles.pageButton,
                                opacity: page === 0 ? 0.5 : 1,
                                cursor: page === 0 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => page > 0 && setPage(page - 1)}
                            disabled={page === 0}
                        >
                            Previous
                        </button>

                        <div style={styles.pageInfo}>
                            Page {page + 1} of {totalPages}
                        </div>

                        <button
                            style={{
                                ...styles.pageButton,
                                opacity: page >= totalPages - 1 ? 0.5 : 1,
                                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => page < totalPages - 1 && setPage(page + 1)}
                            disabled={page >= totalPages - 1}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LwinReferences;

